import React from "react";
import { QRCodeCanvas } from "qrcode.react";
import { CARD_W, CARD_H, resolveLayout, resolveTextContent } from "@/lib/cardLayout";

/**
 * Renderiza el carné con el layout y la config del club.
 * Si onSelectElement está definido y editable=true, los elementos son interactivos
 * (drag/resize via react-rnd se gestiona desde el diseñador, este componente solo pinta).
 */
export default function MemberCard({ cfg, socio, scale = 1, refForward, dimEmpty = false }) {
  const layout = resolveLayout(cfg?.card_layout);
  const bg = cfg?.background_image_url
    ? `url(${cfg.background_image_url}) center/cover no-repeat`
    : `linear-gradient(140deg, ${cfg.primary_color} 0%, ${cfg.secondary_color} 60%, #0A0A0A 100%)`;

  const card = {
    width: CARD_W * scale,
    height: CARD_H * scale,
    background: bg,
    color: cfg.card_text_color,
    borderRadius: 16 * scale,
    position: "relative",
    overflow: "hidden",
    boxShadow: "0 24px 60px rgba(15, 23, 42, 0.25)",
    fontFamily: "'Barlow Condensed', 'DM Sans', sans-serif",
  };

  return (
    <div ref={refForward} style={card} data-testid="member-card">
      {/* Si hay imagen de fondo añadimos un overlay sutil para legibilidad, salvo que se pida mostrarla tal cual */}
      {cfg?.background_image_url && !cfg?.background_image_only && (
        <div
          style={{
            position: "absolute", inset: 0,
            background: `linear-gradient(140deg, ${cfg.primary_color}55 0%, ${cfg.secondary_color}aa 100%)`,
          }}
        />
      )}

      {Object.entries(layout).map(([key, el]) => {
        if (!el.visible && !dimEmpty) return null;
        const baseStyle = {
          position: "absolute",
          left: el.x * scale,
          top: el.y * scale,
          zIndex: el.z || 4,
          opacity: el.opacity ?? 1,
          color: cfg.card_text_color,
        };
        return (
          <ElementRenderer
            key={key}
            elementKey={key}
            el={el}
            cfg={cfg}
            socio={socio}
            scale={scale}
            baseStyle={baseStyle}
          />
        );
      })}
    </div>
  );
}

function ElementRenderer({ elementKey, el, cfg, socio, scale, baseStyle }) {
  if (el.type === "image") {
    const url = elementKey === "logo" ? cfg.logo_url : cfg.secondary_logo_url;
    if (!url) {
      return (
        <div
          style={{
            ...baseStyle,
            width: el.width * scale, height: el.height * scale,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: "rgba(255,255,255,0.08)", border: "2px dashed rgba(255,255,255,0.3)",
            borderRadius: 8 * scale, fontSize: 10 * scale, textAlign: "center", padding: 4 * scale,
          }}
        >
          {elementKey === "logo" ? "LOGO" : "LOGO 2"}
        </div>
      );
    }
    return (
      <img
        src={url}
        crossOrigin="anonymous"
        alt=""
        style={{
          ...baseStyle,
          width: el.width * scale, height: el.height * scale,
          objectFit: "contain",
          background: "rgba(255,255,255,0.08)",
          padding: 4 * scale,
          borderRadius: 8 * scale,
        }}
      />
    );
  }

  if (el.type === "photo") {
    const url = socio?.foto_url;
    if (!url) {
      return (
        <div
          style={{
            ...baseStyle,
            width: el.width * scale, height: el.height * scale,
            background: "rgba(255,255,255,0.08)", border: "2px dashed rgba(255,255,255,0.3)",
            borderRadius: 8 * scale, display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: 11 * scale, fontWeight: 700, letterSpacing: 2,
          }}
        >
          FOTO
        </div>
      );
    }
    return (
      <img
        src={url}
        crossOrigin="anonymous"
        alt=""
        style={{
          ...baseStyle,
          width: el.width * scale, height: el.height * scale,
          objectFit: "cover", borderRadius: 8 * scale,
        }}
      />
    );
  }

  if (el.type === "qr") {
    const value = socio?.qr_code || "SOCIO-PREVIEW";
    const displaySize = el.size * scale;
    // Renderizamos el canvas del QR a 4× la resolución visual para que conserve
    // nitidez al ser escalado por html-to-image al descargar el carné.
    const renderSize = Math.round(displaySize * 4);
    return (
      <div
        style={{
          ...baseStyle,
          background: "#fff",
          padding: 6 * scale,
          borderRadius: 6 * scale,
          lineHeight: 0,
        }}
      >
        <QRCodeCanvas
          value={value}
          size={renderSize}
          level="H"
          includeMargin={false}
          style={{ width: displaySize, height: displaySize, display: "block" }}
        />
      </div>
    );
  }

  // text
  const text = resolveTextContent(el, socio || {}, cfg);
  return (
    <div
      style={{
        ...baseStyle,
        fontSize: el.fontSize * scale,
        fontWeight: el.weight || 500,
        textAlign: el.align || "left",
        letterSpacing: el.letterSpacing ? el.letterSpacing * scale * 0.4 : 0,
        textTransform: el.uppercase === false ? "none" : (el.fontSize >= 18 || el.letterSpacing ? "uppercase" : "none"),
        whiteSpace: "nowrap",
        lineHeight: 1.1,
      }}
    >
      {text}
    </div>
  );
}
